import { getBackendUrl } from '../config/api';
import React from 'react';
import { Download, BookOpen, FileText } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const UserManual = () => {
  const API = getBackendUrl() || '';

  const handleDownload = async () => {
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        toast.error('Please login to download the manual');
        return;
      }

      // Download with authentication
      const response = await axios.get(`${API}/api/download-manual`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        responseType: 'blob' // Important for file download
      });

      // Create a blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'Smart_Manager_User_Manual.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Manual downloaded successfully!');
    } catch (error) {
      console.error('Error downloading manual:', error);
      toast.error('Failed to download manual. Please try again.');
    }
  };

  return (
    <div className="container mx-auto p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white p-8 rounded-t-lg">
          <div className="flex items-center mb-4">
            <BookOpen className="w-12 h-12 mr-4" />
            <div>
              <h1 className="text-3xl font-bold">Smart Manager User Manual</h1>
              <p className="text-blue-100 mt-2">Comprehensive guide for all system features</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white shadow-lg rounded-b-lg p-8">
          {/* Quick Info */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="text-blue-600 font-semibold mb-2">Version</div>
              <div className="text-2xl font-bold">1.0</div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="text-green-600 font-semibold mb-2">Pages</div>
              <div className="text-2xl font-bold">140+</div>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <div className="text-purple-600 font-semibold mb-2">Last Updated</div>
              <div className="text-2xl font-bold">Oct 2025</div>
            </div>
          </div>

          {/* Description */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
              <FileText className="w-6 h-6 mr-2 text-blue-600" />
              What's Included
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border-l-4 border-blue-500 pl-4 py-2">
                <h3 className="font-semibold text-gray-800">Quick Start Guide</h3>
                <p className="text-gray-600 text-sm">Get started in minutes with common tasks</p>
              </div>
              <div className="border-l-4 border-green-500 pl-4 py-2">
                <h3 className="font-semibold text-gray-800">Complete Module Documentation</h3>
                <p className="text-gray-600 text-sm">All features explained step-by-step</p>
              </div>
              <div className="border-l-4 border-purple-500 pl-4 py-2">
                <h3 className="font-semibold text-gray-800">Troubleshooting Guide</h3>
                <p className="text-gray-600 text-sm">Solutions to common issues</p>
              </div>
              <div className="border-l-4 border-orange-500 pl-4 py-2">
                <h3 className="font-semibold text-gray-800">FAQs</h3>
                <p className="text-gray-600 text-sm">30+ frequently asked questions</p>
              </div>
            </div>
          </div>

          {/* Modules Covered */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Modules Covered</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {[
                'Dashboard',
                'Employee Management',
                'Admin Permissions',
                'Activity Logs',
                'Backup Management',
                'Batch Management',
                'Subject Management',
                'Classroom Management',
                'Session Management',
                'Holiday Management',
                'Time Slots',
                'Smart Builder',
                'Smart Timetable',
                'Payroll Management',
                'Leave Management',
                'Salary Slips',
                'Expense Management',
                'Reports & Analytics'
              ].map((module, index) => (
                <div key={index} className="bg-gray-50 px-3 py-2 rounded text-sm text-gray-700 flex items-center">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                  {module}
                </div>
              ))}
            </div>
          </div>

          {/* Download Section */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg border-2 border-blue-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">Download Full Manual</h3>
                <p className="text-gray-600">
                  Complete PDF with all instructions, screenshots, and troubleshooting guides
                </p>
                <div className="mt-3 flex items-center text-sm text-gray-500">
                  <FileText className="w-4 h-4 mr-1" />
                  PDF Format • 105 KB • Ready to print
                </div>
              </div>
              <button
                onClick={handleDownload}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold flex items-center transition-all shadow-lg hover:shadow-xl"
              >
                <Download className="w-5 h-5 mr-2" />
                Download PDF
              </button>
            </div>
          </div>

          {/* Additional Info */}
          <div className="mt-8 bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded">
            <h4 className="font-semibold text-yellow-800 mb-2">📌 Important Notes</h4>
            <ul className="text-sm text-yellow-700 space-y-1">
              <li>• This manual is confidential and for authorized users only</li>
              <li>• Do not share login credentials mentioned in examples</li>
              <li>• Contact IT support if you need additional help</li>
              <li>• Manual is updated regularly - check for latest version</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserManual;
